import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Sparkles, Leaf, Snowflake, HeartHandshake, Calendar, Clock, MapPin, Star, Quote } from 'lucide-react';
import HeroSlideshow from '../components/HeroSlideshow';
import Reveal from '../components/Reveal';
import { brands, categories, values, grandOpening, testimonials } from '../mock';

const valueIcons = [Sparkles, Leaf, Snowflake, HeartHandshake];

const SectionEyebrow = ({ children }) => (
  <p className="text-[#c8a24c] text-xs md:text-sm font-semibold tracking-[0.3em] mb-3 uppercase">
    {children}
  </p>
);

const Home = () => {
  return (
    <div>
      <HeroSlideshow />

      {/* Grand Opening Feature */}
      <section id="grand-opening" className="relative py-20 md:py-28 bg-[#0f2c1f] overflow-hidden">
        <div className="absolute inset-0 opacity-[0.06]" style={{ backgroundImage: 'radial-gradient(circle at 20% 30%, #c8a24c 0%, transparent 40%), radial-gradient(circle at 80% 70%, #c8a24c 0%, transparent 40%)' }} />
        <div className="relative max-w-6xl mx-auto px-5 md:px-8">
          <Reveal className="text-center mb-12">
            <SectionEyebrow>{grandOpening.anniversary}</SectionEyebrow>
            <h2 className="font-serif-display text-white text-4xl md:text-6xl font-bold">
              {grandOpening.title}
            </h2>
            <p className="font-serif-display italic text-[#dcbf74] text-2xl md:text-3xl mt-2">
              {grandOpening.subtitle}
            </p>
            <p className="text-white/70 max-w-2xl mx-auto mt-5">
              {grandOpening.tagline}
            </p>
          </Reveal>

          <Reveal>
            <div className="rounded-3xl border border-[#c8a24c]/30 bg-gradient-to-br from-[#163a29] to-[#0f2c1f] p-8 md:p-12 shadow-2xl">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-8 md:gap-6">
                <div className="text-center md:border-r border-white/10">
                  <div className="w-14 h-14 mx-auto rounded-full bg-[#c8a24c]/15 flex items-center justify-center mb-4">
                    <Calendar className="text-[#c8a24c]" size={24} />
                  </div>
                  <p className="text-white/50 text-xs tracking-[0.2em] uppercase mb-1">Date</p>
                  <p className="text-white font-semibold text-lg">{grandOpening.date}</p>
                </div>
                <div className="text-center md:border-r border-white/10">
                  <div className="w-14 h-14 mx-auto rounded-full bg-[#c8a24c]/15 flex items-center justify-center mb-4">
                    <Clock className="text-[#c8a24c]" size={24} />
                  </div>
                  <p className="text-white/50 text-xs tracking-[0.2em] uppercase mb-1">Time</p>
                  <p className="text-white font-semibold text-lg">{grandOpening.time}</p>
                </div>
                <div className="text-center">
                  <div className="w-14 h-14 mx-auto rounded-full bg-[#c8a24c]/15 flex items-center justify-center mb-4">
                    <MapPin className="text-[#c8a24c]" size={24} />
                  </div>
                  <p className="text-white/50 text-xs tracking-[0.2em] uppercase mb-1">Venue</p>
                  <p className="text-white font-semibold text-base leading-snug">{grandOpening.venue}</p>
                </div>
              </div>

              <div className="mt-10 pt-8 border-t border-white/10 flex flex-col md:flex-row items-center justify-between gap-6">
                <div className="text-center md:text-left">
                  <p className="text-[#c8a24c] text-sm tracking-[0.2em] uppercase">{grandOpening.guestRole}</p>
                  <p className="font-serif-display text-white text-3xl italic">{grandOpening.guest}</p>
                  <p className="text-white/50 text-sm mt-1">Organised by {grandOpening.organiser}</p>
                </div>
                <Link
                  to="/stores"
                  className="group inline-flex items-center gap-2 px-8 py-3.5 rounded-full bg-[#c8a24c] text-[#0f2c1f] font-semibold hover:bg-[#dcbf74] transition-all duration-300"
                >
                  Get Directions
                  <ArrowRight size={18} className="group-hover:translate-x-1 transition-transform" />
                </Link>
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      {/* Categories */}
      <section className="py-20 md:py-28 bg-[#faf7f0]">
        <div className="max-w-7xl mx-auto px-5 md:px-8">
          <Reveal className="text-center mb-14">
            <SectionEyebrow>Explore Our Aisles</SectionEyebrow>
            <h2 className="font-serif-display text-[#163a29] text-4xl md:text-5xl font-bold">
              A World of Authentic Flavours
            </h2>
            <p className="text-[#3a4a42] max-w-2xl mx-auto mt-4">
              From aromatic hand-picked spices to farm-fresh produce, discover curated collections crafted for traditional kitchens.
            </p>
          </Reveal>

          <div className="grid grid-cols-2 md:grid-cols-3 gap-5 md:gap-7">
            {categories.map((cat, i) => (
              <Reveal key={cat.name} delay={i * 80}>
                <div className="img-zoom-parent relative rounded-2xl overflow-hidden h-56 md:h-72 lift-card group cursor-pointer">
                  <img src={cat.image} alt={cat.name} className="img-zoom absolute inset-0 w-full h-full object-cover" />
                  <div className="absolute inset-0 bg-gradient-to-t from-[#0f2c1f]/90 via-[#0f2c1f]/20 to-transparent" />
                  <div className="absolute bottom-0 left-0 p-5">
                    <p className="text-[#dcbf74] text-xs tracking-wider mb-1">{cat.count}</p>
                    <h3 className="font-serif-display text-white text-xl md:text-2xl font-semibold">{cat.name}</h3>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Brands strip */}
      <section className="py-20 md:py-28 bg-white">
        <div className="max-w-7xl mx-auto px-5 md:px-8">
          <Reveal className="text-center mb-14">
            <SectionEyebrow>Exclusive House Signatures</SectionEyebrow>
            <h2 className="font-serif-display text-[#163a29] text-4xl md:text-5xl font-bold">
              Our Flagship Brands
            </h2>
          </Reveal>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {brands.map((b, i) => (
              <Reveal key={b.id} delay={i * 90}>
                <div className="img-zoom-parent bg-[#faf7f0] rounded-2xl overflow-hidden lift-card h-full flex flex-col">
                  <div className="relative h-48 overflow-hidden">
                    <img src={b.image} alt={b.name} className="img-zoom w-full h-full object-cover" />
                    <span className="absolute top-3 left-3 px-3 py-1 rounded-full bg-[#0f2c1f]/90 text-[#c8a24c] text-[10px] font-semibold tracking-widest">{b.tag}</span>
                  </div>
                  <div className="p-6 flex flex-col flex-1">
                    <h3 className="font-serif-display text-[#163a29] text-xl font-semibold mb-2">{b.name}</h3>
                    <p className="text-[#3a4a42] text-sm leading-relaxed">{b.description}</p>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
          <div className="text-center mt-12">
            <Link to="/brands" className="group inline-flex items-center gap-2 px-8 py-3.5 rounded-full bg-[#163a29] text-white font-semibold hover:bg-[#0f2c1f] transition-all duration-300">
              View All Brands
              <ArrowRight size={18} className="group-hover:translate-x-1 transition-transform" />
            </Link>
          </div>
        </div>
      </section>

      {/* Values */}
      <section className="py-20 md:py-28 bg-[#163a29]">
        <div className="max-w-7xl mx-auto px-5 md:px-8">
          <Reveal className="text-center mb-14">
            <SectionEyebrow>Why Choose Us</SectionEyebrow>
            <h2 className="font-serif-display text-white text-4xl md:text-5xl font-bold">
              The Standard of Excellence
            </h2>
          </Reveal>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {values.map((v, i) => {
              const Icon = valueIcons[i];
              return (
                <Reveal key={v.title} delay={i * 90}>
                  <div className="h-full text-center bg-[#0f2c1f]/60 border border-white/10 rounded-2xl p-8 hover:border-[#c8a24c]/50 transition-colors duration-400">
                    <div className="w-16 h-16 mx-auto rounded-full bg-[#c8a24c]/15 flex items-center justify-center mb-5">
                      <Icon className="text-[#c8a24c]" size={28} />
                    </div>
                    <h3 className="font-serif-display text-white text-xl font-semibold mb-3">{v.title}</h3>
                    <p className="text-white/60 text-sm leading-relaxed">{v.desc}</p>
                  </div>
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-20 md:py-28 bg-[#faf7f0]">
        <div className="max-w-7xl mx-auto px-5 md:px-8">
          <Reveal className="text-center mb-14">
            <SectionEyebrow>Loved by Families</SectionEyebrow>
            <h2 className="font-serif-display text-[#163a29] text-4xl md:text-5xl font-bold">
              What Our Community Says
            </h2>
          </Reveal>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {testimonials.map((t, i) => (
              <Reveal key={t.name} delay={i * 100}>
                <div className="h-full bg-white rounded-2xl p-8 shadow-sm border border-[#e8e0cf] lift-card">
                  <Quote className="text-[#c8a24c] mb-4" size={32} />
                  <p className="text-[#3a4a42] leading-relaxed mb-6 italic">“{t.text}”</p>
                  <div className="flex items-center gap-1 mb-3">
                    {[...Array(5)].map((_, s) => (
                      <Star key={s} size={16} className="text-[#c8a24c] fill-[#c8a24c]" />
                    ))}
                  </div>
                  <div>
                    <p className="font-semibold text-[#163a29]">{t.name}</p>
                    <p className="text-sm text-[#7a887f]">{t.location}</p>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 md:py-24 bg-[#0f2c1f]">
        <div className="max-w-4xl mx-auto px-5 text-center">
          <Reveal>
            <h2 className="font-serif-display text-white text-3xl md:text-5xl font-bold mb-5">
              Have a wholesale or custom enquiry?
            </h2>
            <p className="text-white/70 mb-9 max-w-xl mx-auto">
              Reach out to our central digital desk and our team will get back to you promptly.
            </p>
            <Link to="/contact" className="group inline-flex items-center gap-2 px-9 py-4 rounded-full bg-[#c8a24c] text-[#0f2c1f] font-semibold hover:bg-[#dcbf74] transition-all duration-300">
              Get in Touch
              <ArrowRight size={18} className="group-hover:translate-x-1 transition-transform" />
            </Link>
          </Reveal>
        </div>
      </section>
    </div>
  );
};

export default Home;
